// Flexbox Mixins
// http://philipwalton.github.io/solved-by-flexbox/
// https://github.com/philipwalton/solved-by-flexbox
//
// Copyright (c) 2013 Brian Franco
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to
// permit persons to whom the Software is furnished to do so, subject to
// the following conditions:
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
// IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
// CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
// SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
//
// This is a set of mixins for those who want to mess around with flexbox
// using the native support of current browsers. For full support table
// check: http://caniuse.com/flexbox
//
// Basically this will use:
//
// * Fallback, old syntax (IE10, mobile webkit browsers - no wrapping)
// * Final standards syntax (FF, Safari, Chrome, IE11, Opera)
//
// This was inspired by:
//
// * http://dev.opera.com/articles/view/advanced-cross-browser-flexbox/
//
// With need-help from:
//
// * http://w3.org/tr/css3-flexbox/
// * http://the-echoplex.net/flexyboxes/
// * http://msdn.microsoft.com/en-us/library/ie/hh772069(v=vs.85).aspx
// * http://css-tricks.com/using-flexbox/
// * http://dev.opera.com/articles/view/advanced-cross-browser-flexbox/
// * https://developer.mozilla.org/en-us/docs/web/guide/css/flexible_boxes

//----------------------------------------------------------------------

// Flexbox Containers
//
// The 'flex' value causes an element to generate a block-level flex
// container box.
//
// The 'inline-flex' value causes an element to generate a inline-level
// flex container box.
//
// display: flex | inline-flex
//
// http://w3.org/tr/css3-flexbox/#flex-containers
//
// (Placeholder selectors for each type, for those who rather @extend)


const flexbox = () => `
    display: -webkit-box;
    display: -webkit-flex;
    display: -moz-flex;
    display: -ms-flexbox;
    display: flex;
`;

const inlineFlexbox = () => `
    display: -webkit-inline-box;
    display: -webkit-inline-flex;
    display: -moz-inline-flex;
    display: -ms-inline-flexbox;
    display: inline-flex;
`;


//----------------------------------------------------------------------

// Flexbox Direction
//
// The 'flex-direction' property specifies how flex items are placed in
// the flex container, by setting the direction of the flex container's
// main axis. This determines the direction that flex items are laid out in.
//
// Values: row | row-reverse | column | column-reverse
// Default: row
//
// http://w3.org/tr/css3-flexbox/#flex-direction-property

const flexDirection = (value) => {
    const direction = String(value || 'row').toLowerCase();

    let styles = '';
    if (direction === 'row-reverse') {
        styles += `
        -webkit-box-direction: reverse;
        -webkit-box-orient: horizontal;
        `;
    } else if (direction === 'column') {
        styles += `
        -webkit-box-direction: normal;
        -webkit-box-orient: vertical;
        `;
    }
    else if (direction === 'column-reverse') {
        styles += `
        -webkit-box-direction: reverse;
        -webkit-box-orient: vertical;
        `;
    } else {
        styles += `
        -webkit-box-direction: normal;
        -webkit-box-orient: horizontal;
        `;
    }

    styles += `
    -webkit-flex-direction: ${direction};
    -moz-flex-direction: ${direction};
    -ms-flex-direction: ${direction};
    flex-direction: ${direction};
    `;
    return styles;
}

//----------------------------------------------------------------------

// Flexbox Wrap
//
// The 'flex-wrap' property controls whether the flex container is single-line
// or multi-line, and the direction of the cross-axis, which determines
// the direction new lines are stacked in.
//
// Values: nowrap | wrap | wrap-reverse
// Default: nowrap
//
// http://w3.org/tr/css3-flexbox/#flex-wrap-property

const flexWrap = (value = 'nowrap') => {
    const givenValue = String(value).toLowerCase();

    let styles = '';
    if (givenValue === 'nowrap') {
        styles += `
        -ms-flex-wrap: none;
        `;
    } else {
        styles += `
        -ms-flex-wrap: ${givenValue};
        `;
    }
    styles += `
    flex-wrap: ${givenValue};
    `;
    return styles;
}

//----------------------------------------------------------------------

// Flexbox Flow (shorthand)
//
// The 'flex-flow' property is a shorthand for setting the 'flex-direction'
// and 'flex-wrap' properties, which together define the flex container's
// main and cross axes.
//
// Values: <flex-direction> | <flex-wrap>
// Default: row nowrap
//
// http://w3.org/tr/css3-flexbox/#flex-flow-property

// No Webkit Box fallback.
const flexFlow = (value = 'row nowrap') => {
    const givenValue = String(value).toLowerCase();

    let styles = '';

    styles += `
    -webkit-flex-flow: ${value};
    -moz-flex-flow: ${value};
    -ms-flex-flow: ${value};
    flex-flow: ${value};
    `;
    return styles;
}


// Flexbox Order
//
// The 'order' property controls the order in which flex items appear within
// their flex container, by assigning them to ordinal groups.
//
// Default: 0
//
// http://w3.org/tr/css3-flexbox/#order-property

const order = (value = 0) => {
    return `
    -webkit-box-ordinal-group: ${value} + 1;
    -webkit-order: ${value};
    -moz-order: ${value};
    -ms-flex-order: ${value};
    order: ${value};
    `;
}

// Flexbox Grow
//
// The 'flex-grow' property sets the flex grow factor. Negative numbers
// are invalid.
//
// Default: 0
//
// http://w3.org/tr/css3-flexbox/#flex-grow-property

const flexGrow = (value = 0) => {
    return `
    -webkit-box-flex: ${value};
    -webkit-flex-grow: ${value};
    -moz-flex-grow: ${value};
    -ms-flex-positive: ${value};
    flex-grow: ${value};
    `;
}

// Flexbox Shrink
//
// The 'flex-shrink' property sets the flex shrink factor. Negative numbers
// are invalid.
//
// Default: 1
//
// http://w3.org/tr/css3-flexbox/#flex-shrink-property

const flexShrink = (value = 1) => {
    return `
    -webkit-flex-shrink: ${value};
    -moz-flex-shrink: ${value};
    -ms-flex-negative: ${value};
    flex-shrink: ${value};
    `;
}

//----------------------------------------------------------------------

// Flexbox Basis
//
// The 'flex-basis' property sets the flex basis. Negative lengths are invalid.
//
// Values: Like "width"
// Default: auto
//
// http://www.w3.org/TR/css3-flexbox/#flex-basis-property

const flexBasis = (value = 'auto') => {
    return `
    -webkit-flex-basis: ${value};
    -moz-flex-basis: ${value};
    -ms-flex-preferred-size: ${value};
    flex-basis: ${value};
    `;
}


// Flexbox "Flex" (shorthand)
//
// The 'flex' property specifies the components of a flexible length: the
// flex grow factor and flex shrink factor, and the flex basis. When an
// element is a flex item, 'flex' is consulted instead of the main size
// property to determine the main size of the element. If an element is
// not a flex item, 'flex' has no effect.
//
// Values: none | <flex-grow> <flex-shrink> || <flex-basis>
// Default: See individual properties (1 1 0).
//
// http://w3.org/tr/css3-flexbox/#flex-property

const flex = (fg = '1', fs = '', fb = '') => {
    // Set a variable to be used by box-flex properties
    let fgBoxflex = fg;

    const fgArr = String(fg).split(' ');
    if (fgArr.length > 1) {
        fgBoxflex = fgArr[0];
    }

    return `
    -webkit-box-flex: ${fgBoxflex};
    -webkit-flex: ${fg} ${fs} ${fb};
    -moz-box-flex: ${fgBoxflex};
    -moz-flex: ${fg} ${fs} ${fb}|;
    -ms-flex: ${fg} ${fs} ${fb};
    flex: ${fg} ${fs} ${fb};
    `;
}

//----------------------------------------------------------------------

// Flexbox Justify Content
//
// The 'justify-content' property aligns flex items along the main axis
// of the current line of the flex container. This is done after any flexible
// lengths and any auto margins have been resolved. Typically it helps distribute
// extra free space leftover when either all the flex items on a line are
// inflexible, or are flexible but have reached their maximum size. It also
// exerts some control over the alignment of items when they overflow the line.
//
// Note: 'space-*' values not supported in older syntaxes.
//
// Values: flex-start | flex-end | center | space-between | space-around
// Default: flex-start
//
// http://w3.org/tr/css3-flexbox/#justify-content-property

const justifyContent = (value = 'flex-start') => {
    const givenValue = String(value).toLowerCase();
    let styles = '';
    if (givenValue === 'flex-start') {
        styles += `
        -webkit-box-pack: start;
        -ms-flex-pack: start;
        `;
    } else if (givenValue === 'flex-end') {
        styles += `
        -webkit-box-pack: end;
        -ms-flex-pack: end;
        `;
    } else if (givenValue === 'space-between') {
        styles += `
        -webkit-box-pack: justify;
        -ms-flex-pack: justify;
        `;
    } else if (givenValue === 'space-around') {
        styles += `
        -ms-flex-pack: distribute;
        `;
    } else {
        styles += `
        -webkit-box-pack: ${givenValue};
        -ms-flex-pack: ${givenValue};
        `;
    }

    styles += `
    -webkit-justify-content: ${givenValue};
    -moz-justify-content: ${givenValue};
    justify-content: ${givenValue};
    `;
    return styles;
}

//----------------------------------------------------------------------

// Flexbox Align Items
//
// Flex items can be aligned in the cross axis of the current line of the
// flex container, similar to 'justify-content' but in the perpendicular
// direction. 'align-items' sets the default alignment for all of the flex
// container's items, including anonymous flex items. 'align-self' allows
// this default alignment to be overridden for individual flex items. (For
// anonymous flex items, 'align-self' always matches the value of 'align-items'
// on their associated flex container.)
//
// Values: flex-start | flex-end | center | baseline | stretch
// Default: stretch
//
// http://w3.org/tr/css3-flexbox/#align-items-property

const alignItems = (value = 'stretch') => {
    const givenValue = String(value).toLowerCase();
    let styles = '';

    if (givenValue === 'flex-start') {
        styles += `
        -webkit-box-align: start;
        -ms-flex-align: start;
        `;
    } else if (givenValue === 'flex-end') {
        styles += `
        -webkit-box-align: end;
        -ms-flex-align: end;
        `;
    } else {
        styles += `
        -webkit-box-align: ${givenValue};
        -ms-flex-align: ${givenValue};
        `;
    }

    styles += `
    -webkit-align-items: ${givenValue};
    -moz-align-items: ${givenValue};
    align-items: ${givenValue};
    `;
    return styles;
}


//----------------------------------

// Flexbox Align Self
//
// Values: auto | flex-start | flex-end | center | baseline | stretch
// Default: auto

const alignSelf = (value = 'auto') => {
    const givenValue = String(value).toLowerCase();
    // No Webkit Box Fallback.
    let styles = `
    -webkit-align-self: ${givenValue};
    -moz-align-self: ${givenValue};
    `;

    if (givenValue === 'flex-start') {
        styles += `
        -ms-flex-item-align: start;
        `;
    } else if (givenValue === 'flex-end') {
        styles += `
        -ms-flex-item-align: end;
        `;
    } else {
        styles += `
        -ms-flex-item-align: ${givenValue};
        `;
    }

    styles += `
    align-self: ${givenValue};
    `;
    return styles;
}


//----------------------------------------------------------------------

// Flexbox Align Content
//
// The 'align-content' property aligns a flex container's lines within the
// flex container when there is extra space in the cross-axis, similar to
// how 'justify-content' aligns individual items within the main-axis. Note,
// this property has no effect when the flexbox has only a single line.
//
// Values: flex-start | flex-end | center | space-between | space-around | stretch
// Default: stretch
//
// http://w3.org/tr/css3-flexbox/#align-content-property

const alignContent = (value = 'stretch') => {
    const givenValue = String(value).toLowerCase();
    // No Webkit Box Fallback.
    let styles = `
    -webkit-align-content: ${givenValue};
    -moz-align-content: ${givenValue};
    `;

    if (givenValue === 'flex-start') {
        styles += `
        -ms-flex-line-pack: start;
        `;
    } else if (givenValue === 'flex-end') {
        styles += `
        -ms-flex-line-pack: end;
        `;
    } else {
        styles += `
        -ms-flex-line-pack: ${givenValue};
        `;
    }

    styles += `
    align-content: ${givenValue};
    `;
    return styles;
}

export {
    flexbox,
    inlineFlexbox,
    flexDirection,
    flexWrap,
    flexFlow,
    order,
    flexGrow,
    flexBasis,
    flex,
    justifyContent,
    alignItems,
    alignSelf,
    alignContent,
    flexShrink
};
